import { useEffect, useState } from "react";
import { api, itemImage } from "@/lib/api";
import { LookImage } from "@/components/LookImage";
import { SwapPieceDialog } from "@/components/Lookbook";
import { weatherIcon } from "@/components/WeatherWidget";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Heart, Replace, ThumbsDown, Loader2, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const timeOfDay = () => { const h = new Date().getHours(); return h < 12 ? "morning" : h < 17 ? "afternoon" : "evening"; };
const todayKey = () => `threadlist.daily.${new Date().toISOString().slice(0, 10)}`;

/**
 * Task 10 - the Home hero: one look for today, generated once per session by the existing outfit generator
 * (today's weather + day), never repeating any of the last 7 session looks. Avatar wearing it + Save, Swap piece, Dislike.
 */
export const DailyHero = ({ itemCount }) => {
  const [outfit, setOutfit] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error | empty
  const [picking, setPicking] = useState(false);
  const [swap, setSwap] = useState(null);
  const [renderKey, setRenderKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [disliking, setDisliking] = useState(false);

  const generate = async (dislikeId = null) => {
    setState("loading");
    try {
      const o = await api.dailyOutfit({ time_of_day: timeOfDay(), dislike_outfit_id: dislikeId || undefined });
      setOutfit(o); setRenderKey((k) => k + 1); setState(o?.items?.length ? "ready" : "empty");
      sessionStorage.setItem(todayKey(), o.id);
    } catch (e) {
      if (e?.response?.status === 400) setState("empty"); else { setState("error"); toast.error(e?.response?.data?.detail || "Couldn't style today's look"); }
    }
  };

  useEffect(() => {
    if (itemCount === 0) { setState("empty"); return; }
    let on = true;
    (async () => {
      // same session -> reuse today's look; new session -> generate a fresh one
      if (sessionStorage.getItem(todayKey())) {
        try { const o = await api.dailyOutfitToday(); if (!on) return; if (o?.items?.length) { setOutfit(o); setState("ready"); return; } } catch { /* fall through */ }
      }
      if (on) generate();
    })();
    return () => { on = false; };
  }, [itemCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!outfit) return;
    setSaving(true);
    try { const r = await api.favoriteOutfit(outfit.id); const fav = r?.favorite ?? !outfit.favorite; setOutfit((o) => ({ ...o, favorite: fav })); toast.success(fav ? "Saved to your Lookbook" : "Removed from your Lookbook"); }
    catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };
  const dislike = async () => { setDisliking(true); await generate(outfit?.id); setDisliking(false); setPicking(false); };
  const onSwapped = (o) => { setOutfit(o); setRenderKey((k) => k + 1); setPicking(false); };
  const c = outfit?.weather?.current;

  if (state === "empty") return null;

  return (
    <section className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="daily-hero">
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border">
        <span className="h-8 w-8 rounded-full bg-foreground text-background inline-flex items-center justify-center"><Sparkles className="h-4 w-4" /></span>
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-none">Today's look</p>
          <p className="text-[11px] text-muted-foreground mt-1 truncate">
            {c ? <>{weatherIcon(c.code, "h-3 w-3 inline mr-1")}{Math.round(c.temp)}{c.unit} · {c.description} · </> : null}
            {new Date().toLocaleDateString(undefined, { weekday: "long" })} · styled from your closet{outfit && outfit.fresh === false ? " · closest we could get without repeating" : ""}
          </p>
        </div>
        {state === "ready" && <Button variant="ghost" size="sm" onClick={() => generate()} className="ml-auto rounded-full h-8 text-muted-foreground" title="Another one (spends a generation)" data-testid="daily-hero-regenerate"><RefreshCw className="h-3.5 w-3.5" /></Button>}
      </div>

      <div className="p-5">
        {state === "loading" && (
          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-4" data-testid="daily-hero-loading">
            <Skeleton className="aspect-[3/4] rounded-xl" />
            <div className="space-y-3"><p className="text-sm text-muted-foreground inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Dressing you for today…</p><div className="grid grid-cols-2 gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="aspect-[3/4] rounded-xl" />)}</div></div>
          </div>
        )}
        {state === "error" && (
          <div className="rounded-xl border border-dashed border-border p-8 text-center" data-testid="daily-hero-error">
            <p className="text-sm text-muted-foreground">Couldn't put together today's look.</p>
            <Button variant="outline" onClick={() => generate()} className="mt-3 rounded-full" data-testid="daily-hero-retry">Try again</Button>
          </div>
        )}
        {state === "ready" && outfit && (
          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-4">
            <div className="relative">
              <LookImage key={`${outfit.id}-${renderKey}`} outfitId={outfit.id} className="aspect-[3/4]" testId="daily-hero-look" />
            </div>
            <div className="flex flex-col">
              <p className="eyebrow">{picking ? "Tap the piece to swap" : "The pieces"}</p>
              <div className={`mt-2 grid grid-cols-2 gap-2.5 ${picking ? "ring-2 ring-foreground/20 rounded-xl p-1 -m-1" : ""}`} data-testid="daily-hero-pieces">
                {(outfit.items || []).map((it) => (
                  <button key={it.id} onClick={() => setSwap(it)} className={`group relative text-left rounded-xl overflow-hidden bg-gray-50 border transition-colors ${picking ? "border-foreground animate-pulse" : "border-border hover:border-foreground/40"}`} title={`Swap ${it.name}`} data-testid={`daily-hero-piece-${it.id}`}>
                    <div className="aspect-[3/4]"><img src={itemImage(it)} alt={it.name || ""} className="h-full w-full object-cover mix-blend-multiply" /></div>
                    <p className="absolute bottom-0 inset-x-0 bg-background/90 px-2 py-1 text-[11px] font-medium truncate">{it.name}</p>
                  </button>
                ))}
              </div>
              {outfit.reasoning && <p className="text-xs text-muted-foreground mt-3 line-clamp-3" data-testid="daily-hero-reasoning">{outfit.reasoning}</p>}
              <div className="mt-auto pt-4 flex flex-wrap gap-2">
                <Button onClick={save} disabled={saving} className="rounded-full" data-testid="daily-hero-save"><Heart className={`h-4 w-4 mr-1.5 ${outfit.favorite ? "fill-current" : ""}`} /> {outfit.favorite ? "Saved" : "Save"}</Button>
                <Button variant="outline" onClick={() => setPicking((p) => !p)} className="rounded-full" aria-pressed={picking} data-testid="daily-hero-swap"><Replace className="h-4 w-4 mr-1.5" /> Swap piece</Button>
                <Button variant="ghost" onClick={dislike} disabled={disliking} className="rounded-full text-muted-foreground" data-testid="daily-hero-dislike">{disliking ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ThumbsDown className="h-4 w-4 mr-1.5" />} Dislike</Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {swap && <SwapPieceDialog outfit={outfit} piece={swap} open onOpenChange={(o) => !o && setSwap(null)} onSwapped={onSwapped} />}
    </section>
  );
};
